import { IDestination } from 'features/search-widget';
import { CURATED_DESTINATIONS, ICuratedDestination } from 'features/search-widget/config/curated-destinations.config';

// ESS typeahead categorizes results into location types (see uitk-react-typeahead's
// ESSAdapter categorizeResponse/ESSCategories, e.g. CITY, HOTEL, MULTIREGION, ...).
// Curated/passthrough destinations represent a broader area (a region), not a single
// city or hotel, so MULTIREGION is the closest existing convention to reuse here.
const CURATED_DESTINATION_TYPE = 'MULTIREGION';

function getCuratedDestination(name: string): ICuratedDestination | undefined {
  return CURATED_DESTINATIONS[name?.trim().toLowerCase()];
}

function toIDestination(name: string, regionId: string, location?: { lat: string; long: string }): IDestination {
  return {
    term: name,
    data: {
      type: CURATED_DESTINATION_TYPE,
      regionId,
      regionNames: {
        fullName: name,
        shortName: name,
      },
      ...(location && { location }),
    },
  };
}

// Resolves the raw `data-destination` embed attribute into an IDestination.
//
// - Exactly 1 part (name only) -> V1: resolve via the curated name->ID map.
// - Exactly 2 parts (name|regionId) -> V2: trust the passthrough pair directly,
//   no curated-map lookup.
// - 0 parts (empty/undefined) or 3+ parts (malformed) -> undefined; the normal
//   ESS Typeahead flow owns the destination.
function resolveDestinationParam(raw: string | undefined): IDestination | undefined {
  // routeQueries is typed as Record<string, string>, but a repeated query param
  // (e.g. ?destination=a&destination=b) parses to a string[] at runtime -- the same
  // edge case getRequestedLobsConfig guards against for the sibling `lobs` param.
  if (!raw || typeof raw !== 'string') {
    return undefined;
  }

  const parts = raw.split('|');

  if (parts.length === 1) {
    const [name] = parts;
    const curatedDestination = getCuratedDestination(name);

    if (!curatedDestination) {
      return undefined;
    }

    return toIDestination(curatedDestination.name, curatedDestination.id, {
      lat: curatedDestination.lat,
      long: curatedDestination.long,
    });
  }

  if (parts.length === 2) {
    const [name, regionId] = parts;
    const trimmedName = name.trim();
    const trimmedRegionId = regionId.trim();

    if (!trimmedName || !trimmedRegionId) {
      // eslint-disable-next-line no-console
      console.warn(
        `[destination.util] malformed data-destination value "${raw}" -- falling back to normal typeahead flow`
      );

      return undefined;
    }

    return toIDestination(trimmedName, trimmedRegionId);
  }

  // eslint-disable-next-line no-console
  console.warn(`[destination.util] malformed data-destination value "${raw}" -- falling back to normal typeahead flow`);

  return undefined;
}

export { getCuratedDestination, resolveDestinationParam };
